import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { b2bGetList, b2bUpdateList, b2bRemoveListMember, b2bAddListMembers, b2bGetContacts } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

export default function B2BListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [addSearch, setAddSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);

  function load() {
    setLoading(true);
    b2bGetList(id).then(r => { setData(r); setForm({ list_name: r.list.list_name, description: r.list.description || '' }); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, [id]);

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    try { await b2bUpdateList(id, form); setEditing(false); load(); }
    catch (err) { alert(err.message); }
    setSaving(false);
  }

  async function openAdd() {
    setShowAdd(true); setSelected(new Set());
    b2bGetContacts().then(r => setAllContacts(r.contacts || [])).catch(() => {});
  }

  async function handleAdd() {
    if (!selected.size) return;
    setAdding(true);
    try { await b2bAddListMembers(id, [...selected]); setShowAdd(false); load(); }
    catch (err) { alert(err.message); }
    setAdding(false);
  }

  function toggleSelect(cid) {
    setSelected(s => { const ns = new Set(s); ns.has(cid) ? ns.delete(cid) : ns.add(cid); return ns; });
  }

  if (loading) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-lists" /><main style={{ padding: 40, color: '#64748b' }}>Loading…</main></div>;

  const existingIds = new Set((data?.members || []).map(m => m.contact_id));
  const filteredAll = allContacts.filter(c => {
    if (existingIds.has(c.contact_id)) return false;
    if (!addSearch) return true;
    const q = addSearch.toLowerCase();
    return [c.first_name, c.last_name, c.email, c.company_name].some(v => v?.toLowerCase().includes(q));
  });

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-lists" />
      <main style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/b2b/lists')}>← Lists</button>

        <div style={s.header}>
          <div>
            <h1 style={s.title}>{data?.list.list_name}</h1>
            {data?.list.description && <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{data.list.description}</div>}
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{data?.members.length} members</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.primaryBtn} onClick={openAdd}>+ Add Contacts</button>
            <button style={s.editBtn} onClick={() => setEditing(e => !e)}>{editing ? 'Cancel' : 'Edit'}</button>
          </div>
        </div>

        {editing && (
          <div style={{ ...s.card, marginBottom: 24 }}>
            <form onSubmit={handleSave} style={{ display: 'grid', gap: 12, padding: 20 }}>
              <label style={s.label}>List Name<input required style={s.inp} value={form.list_name} onChange={e => setForm(f => ({ ...f, list_name: e.target.value }))} /></label>
              <label style={s.label}>Description<textarea style={{ ...s.inp, height: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></label>
              <button type="submit" style={{ ...s.primaryBtn, width: 'fit-content' }} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </form>
          </div>
        )}

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>{['Name', 'Company', 'Title', 'Email', 'Added', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data?.members.length === 0 && <tr><td colSpan={6} style={s.empty}>No members yet. Add contacts to get started.</td></tr>}
              {data?.members.map(c => (
                <tr key={c.contact_id} style={s.tr}>
                  <td style={{ ...s.td, fontWeight: 500, color: NAVY }}><Link to={`/admin/b2b/contacts/${c.contact_id}`} style={{ color: NAVY, textDecoration: 'none' }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</Link></td>
                  <td style={s.td}>{c.company_name || '—'}</td>
                  <td style={{ ...s.td, color: '#64748b' }}>{c.title || '—'}</td>
                  <td style={{ ...s.td, color: BLUE, fontSize: 12 }}>{c.email}</td>
                  <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{new Date(c.added_on_utc).toLocaleDateString()}</td>
                  <td style={s.td}><button style={s.deleteBtn} onClick={async () => { await b2bRemoveListMember(id, c.contact_id); load(); }}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showAdd && (
          <div style={s.modal}>
            <div style={s.modalBox}>
              <h2 style={{ margin: '0 0 12px', color: NAVY, fontSize: 18 }}>Add Contacts</h2>
              <input style={{ ...s.inp, marginBottom: 12 }} placeholder="Search contacts…" value={addSearch} onChange={e => setAddSearch(e.target.value)} />
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                {filteredAll.length === 0 && <div style={{ padding: 20, color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>No contacts to add</div>}
                {filteredAll.map(c => (
                  <label key={c.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected.has(c.contact_id)} onChange={() => toggleSelect(c.contact_id)} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: NAVY }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{[c.title, c.company_name].filter(Boolean).join(' · ')}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>{selected.size} selected</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={s.cancelBtn} onClick={() => setShowAdd(false)}>Cancel</button>
                  <button style={s.primaryBtn} onClick={handleAdd} disabled={adding || !selected.size}>{adding ? 'Adding…' : `Add ${selected.size || ''}`}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main: { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  backBtn: { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 8px', display: 'block' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: NAVY, fontSize: 24, fontWeight: 700, margin: 0 },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  editBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, cursor: 'pointer', fontSize: 13, padding: '7px 16px' },
  cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  deleteBtn: { background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: 24 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty: { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  label: { color: '#374151', fontSize: 12, fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 4 },
  inp: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', marginTop: 2 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox: { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
};
